import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { catalogFetch } from "./catalog";
import type { BlueprintManifest, BlueprintManifestResult } from "./types";

const MANIFESTS_PATH = "/catalog/blueprint-manifests";

// The live blueprint of one Platform, rendered fresh on every view (ADR-0051).
export function useBlueprintManifestExport(slug: string) {
  return useQuery({
    queryKey: ["blueprint-manifests", slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await catalogFetch(
        `/catalog/platforms/${encodeURIComponent(slug)}/blueprint/manifest`,
      );
      return res.json() as Promise<BlueprintManifest>;
    },
  });
}

async function postManifest(
  action: "plan" | "apply",
  manifest: BlueprintManifest,
) {
  const res = await catalogFetch(`${MANIFESTS_PATH}/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(manifest),
  });
  return res.json() as Promise<BlueprintManifestResult>;
}

// Judges every object in the manifest and writes nothing.
export function planBlueprintManifest(manifest: BlueprintManifest) {
  return postManifest("plan", manifest);
}

// A 200 even on partial success: read the per-object outcomes.
export function useApplyBlueprintManifest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (manifest: BlueprintManifest) =>
      postManifest("apply", manifest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blueprint-manifests"] });
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
    },
  });
}
